import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { nativeLinkTarget, routePath } from '../../appLinks/links';
import { useOnboarding } from '../../onboarding/OnboardingProvider';
import { markExternalEntry, recordEntry } from '../../onboarding/state';

/**
 * Universal Links и явная кнопка «Открыть в WIW» ведут в тот же SPA-маршрут
 * при холодном и повторном запуске. OAuth сохраняет query/hash исключительно
 * для HTTPS callback нашего домена; внешние адреса и произвольные схемы
 * отбрасываются общим правилом nativeLinkTarget.
 */
export default function AppUrlListener() {
  const navigate = useNavigate();
  const { dismiss, launchResolved } = useOnboarding();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let active = true;
    let receivedEvent = false;
    let launchTarget = null;
    let linkedEntry = false;

    const open = (data, fromLaunch = false) => {
      if (!active || typeof data?.url !== 'string' || !data.url.trim()) return;
      // An explicit but unsupported link is still an external entry. Keep the
      // target allowlist intact, without presenting a launcher introduction.
      // A newer live intent also takes precedence over a rejected snapshot.
      if (fromLaunch && receivedEvent) return;
      linkedEntry = true;
      const target = nativeLinkTarget(data.url);
      if (!target) {
        if (!fromLaunch) { receivedEvent = true; launchTarget = null; }
        markExternalEntry();
        dismiss();
        return;
      }
      if (fromLaunch) {
        // A live event is newer than the launch snapshot, even if its URL differs.
        launchTarget = target;
      } else {
        receivedEvent = true;
        const duplicate = target === launchTarget;
        launchTarget = null;
        if (duplicate) return;
      }
      const address = new URL(data.url);
      if (routePath(address.pathname)?.replace(/\/$/, '') !== '/login') markExternalEntry();
      else recordEntry(address, '');
      dismiss();
      navigateRef.current(target);
    };

    // Subscribe first: native may deliver the same link through both channels.
    const listenerPromise = CapacitorApp.addListener('appUrlOpen', data => open(data))
      .catch(() => { console.error('Could not register native link listener'); });
    const isNative = window.Capacitor?.isNativePlatform?.() === true
      || window.location.protocol === 'capacitor:';
    // A stuck bridge must not hold session/content initialization indefinitely.
    // Unknown launch provenance skips teaching, but a late valid link still opens.
    const launchTimer = isNative ? setTimeout(() => { if (active) launchResolved(true); }, 2000) : null;
    if (isNative && typeof CapacitorApp.getLaunchUrl === 'function') {
      CapacitorApp.getLaunchUrl().then(data => open(data, true))
        .then(() => { clearTimeout(launchTimer); if (active) launchResolved(false, linkedEntry); })
        .catch(() => { clearTimeout(launchTimer); if (active) { launchResolved(true); console.error('Could not read native launch link'); } });
    } else { clearTimeout(launchTimer); launchResolved(isNative); }

    return () => {
      active = false;
      clearTimeout(launchTimer);
      listenerPromise.then(listener => listener?.remove())
        .catch(() => { console.error('Could not remove native link listener'); });
    };
  }, [dismiss, launchResolved]);

  return null;
}
